import { type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn.js';
import { EmptyState } from './DataTable.js';

export type SortableListProps<T> = {
  items: readonly T[];
  /**
   * Surish uchun barqaror kalit.
   *
   * `index` ham beriladi: xarakteristika qatorlari va guruhlarida o'z
   * identifikatori yo'q, ular faqat massivdagi o'rni bilan farqlanadi.
   */
  getId: (item: T, index: number) => string;
  /** Yangi tartibdagi TO'LIQ ro'yxat — chaqiruvchi `order` ni o'zi hisoblaydi. */
  onReorder: (items: T[]) => void;
  renderRow: (item: T, index: number) => ReactNode;
  loading?: boolean;
  emptyTitle?: string;
  /** Ro'yxat elementiga qo'shimcha uslub (tanlangan blok belgilanadi). */
  rowClassName?: (item: T, index: number) => string | undefined;
  /**
   * Zich ro'yxat — MAKETDAGI blok konstruktori (`D4`): qatorlar
   * bir-biriga tegib turadi, o'z chegarasi yo'q va faol band chap
   * chekkadagi aksent chiziq bilan belgilanadi.
   */
  dense?: boolean;
};

/**
 * Surib tartiblanadigan ro'yxat.
 *
 * Bitta komponent ikki joyda ishlaydi: kontent ro'yxatlarida (rahbariyat,
 * hujjatlar) va blok konstruktorining chap panelida. Ikki marta yozilsa,
 * biri klaviatura bilan ishlamay qolardi.
 *
 * Klaviatura MAJBURIY: sichqonchasiz ishlaydigan xodim ham tartibni
 * o'zgartira olishi kerak (`sortableKeyboardCoordinates` — probel bilan
 * olib, strelka bilan suriladi).
 */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  renderRow,
  loading = false,
  emptyTitle = 'Hozircha yozuv yo‘q',
  rowClassName,
  dense = false,
}: SortableListProps<T>) {
  const sensors = useSensors(
    // 6px — tasodifiy siljish surish deb qabul qilinmasin: aks holda
    // ro'yxatdagi tugmani bosish ham surishni boshlab yuborardi.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = items.findIndex((item, index) => getId(item, index) === active.id);
    const to = items.findIndex((item, index) => getId(item, index) === over.id);
    if (from === -1 || to === -1) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    if (moved !== undefined) next.splice(to, 0, moved);
    onReorder(next);
  }

  if (loading) {
    return (
      <div className="space-y-2" aria-hidden>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-12 animate-pulse bg-n-100" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className=" border border-n-200 bg-white">
        <EmptyState title={emptyTitle} />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item, index) => getId(item, index))}
        strategy={verticalListSortingStrategy}
      >
        <ul className={dense ? 'flex flex-col gap-px' : 'space-y-2'}>
          {items.map((item, index) => (
            <SortableRow
              key={getId(item, index)}
              id={getId(item, index)}
              dense={dense}
              className={rowClassName?.(item, index)}
            >
              {renderRow(item, index)}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  className,
  dense = false,
  children,
}: {
  id: string;
  className?: string;
  dense?: boolean;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 bg-white',
        dense ? 'h-[50px] border-l-[3px] pr-3 pl-2' : 'border border-n-200 px-2 py-2',
        isDragging && 'z-10',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Surish uchun ushlang"
        className="cursor-grab p-1 text-n-400 hover:bg-n-100 hover:text-n-600 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>

      {children}
    </li>
  );
}
